// usePermission — capability check for the UI (PERMISSIONS REVAMP 2026-08-01).
// The Firestore rules are the real enforcement; this hook is for show/hide/enable + client-side gates.
//
// EFFECTIVE-IDENTITY FIX (Jose 2026-08-17): the authority is WHO IS OPERATING, not the raw login.
// The auth `users` doc role is often just 'staff'/'customer' and does NOT carry owner/manager/admin
// authority — the STAFF RECORD does (role + accessLevel + permissions). So resolve like MainLayout:
// prefer the PIN/station operator's staff record, fall back to the auth account. Without this, an
// Owner/Admin got blocked by a manager-only gate because the hook only saw the auth role.
import { useAuth } from '../contexts/AuthContext';
import { usePinLock } from '../contexts/PinLockContext';
import { barberDataService } from '../services/barberDataService';
import { defaultPermissionsFor, PermissionKey } from '../services/shopMembersService';
import { StaffRole } from '../types/staff';
// ONE AUTHORITY BRAIN (Jose 2026-10-25): owner ⊇ manager ⊇ senior ⊇ barber + per-barber capabilities,
// resolved in /shared/authority.ts so the UI, services, and functions all agree. No raw role checks.
import { resolveAuthority, type Capability } from '../shared/authority';

const STAFF_ROLES: StaffRole[] = ['owner', 'manager', 'barber', 'trainee', 'shop_attendant', 'accountant'];

export function usePermission() {
  const { user } = useAuth();
  const { currentBarber } = usePinLock();

  // The acting operator's staff record (station/PIN identity) takes precedence over the auth account.
  const opBarber = currentBarber?.barberId ? barberDataService.getBarberById(currentBarber.barberId) : null;

  // AUTHORITY SOURCE (Jose 2026-10-21): trust the role/accessLevel/permissions CAPTURED ON THE PIN SESSION
  // first, THEN the loaded barber record, THEN the auth account. The session copy is set at tap-in, so this
  // is correct immediately — before barberDataService finishes loading. Without it, an owner/manager who
  // just tapped in on a freshly-woken iPad briefly read as non-manager (the shared 'staff' base account),
  // which HID the ⚡ manager squeeze toggle → "it won't let me squeeze, says already taken". #130.
  const opRole = (currentBarber as any)?.role || (opBarber as any)?.role;
  const opAccess = (currentBarber as any)?.accessLevel || (opBarber as any)?.accessLevel;
  const opPerms = (currentBarber as any)?.permissions || (opBarber as any)?.permissions;
  const opCaps = (currentBarber as any)?.capabilities || (opBarber as any)?.capabilities;

  const effectiveRoleRaw = String(
    (currentBarber?.barberId ? opRole : (user as any)?.role) || (user as any)?.role || 'customer'
  ).toLowerCase();
  // accessLevel is the permission preset set on a staff/barber profile: barber | senior | manager | admin.
  const accessLevel = String(
    (currentBarber?.barberId ? opAccess : (user as any)?.accessLevel) || (user as any)?.accessLevel || ''
  ).toLowerCase();

  // THE ONE AUTHORITY BRAIN resolves the hierarchy (owner ⊇ manager ⊇ senior ⊇ barber) + the operator's
  // per-barber trust capabilities. No role string is compared here or anywhere else — this is the source.
  const auth = resolveAuthority({
    role: effectiveRoleRaw,
    accessLevel,
    capabilities: (currentBarber?.barberId ? opCaps : (user as any)?.capabilities) || null,
  });
  const { isOwner, isManager, isStaff, can } = auth;
  // 'senior' isn't a StaffRole in the UI type → surface it as 'barber' (its authority still rides on `can`).
  const role = (STAFF_ROLES.includes(auth.role as StaffRole) ? auth.role : (isStaff ? 'barber' : 'customer')) as StaffRole | 'customer';

  // Fine-grained feature permissions map (editSettings/viewReports/…) — owner → all true.
  const rawPerms = opPerms || (user as any)?.shopPermissions || (user as any)?.permissions;
  const map: Record<string, boolean> =
    rawPerms && typeof rawPerms === 'object' && !Array.isArray(rawPerms)
      ? rawPerms
      : (isStaff ? defaultPermissionsFor((STAFF_ROLES.includes(role as StaffRole) ? role : 'barber') as StaffRole) : ({} as any));

  const hasPerm = (key: PermissionKey): boolean => (isOwner ? true : !!map[key]);

  // `can(capability)` → the operational trust gate (overrideConflict / lateCheckout / editPastAppointments…).
  return { role, isOwner, isManager, isStaff, accessLevel, hasPerm, can };
}
